/**** fertilizacionDataCards.jsx ****/
import React from "react";
import { styled } from "@mui/system";
import DataCard from "../../../components/CardData/DataCard";
import CircularProgress from "@mui/material/CircularProgress";
import { displayValue, formatNumber as formatDecimalNumber, roundToDecimals } from "../../../utils/generalUtils";
import ReactEChartsCore from 'echarts-for-react/lib/core';
import * as echarts from 'echarts/core';
import { PieChart, BarChart } from 'echarts/charts';
import {
    GridComponent,
    TooltipComponent,
    LegendComponent,
    GraphicComponent,
} from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import {
    CategoryContainer,
    CardsGrid,
    VariationLabel,
    CategoryTitle,
    ChartContainer,
    AreaTotalContainer,
    ChartsRow,
    VariationContainer,
    VariationDosisContainer,
    VariationValue,
} from "./fertilizacionDataCardsStyle";

echarts.use([
    PieChart, BarChart,
    GridComponent, TooltipComponent, LegendComponent, GraphicComponent,
    CanvasRenderer,
]);

// Colores de marca
const PRIMARY_COLOR = "var(--primary-color)";
const PRIMARY_COLOR_DARK = "var(--primary-color-dark)";
const PRIMARY_COLOR_HEX = '#2563EB';

const glassTooltip = {
    backgroundColor: 'rgba(255,255,255,0.88)',
    borderColor: '#E2E8F0',
    borderWidth: 1,
    padding: [8, 12],
    textStyle: { color: '#334155', fontSize: 13 },
    extraCssText: 'backdrop-filter:blur(6px);border-radius:8px;box-shadow:0 4px 24px rgba(0,0,0,0.08)',
};

function renderCategory(categoryTitle, content, refProp) {
    return (
        <CategoryContainer ref={refProp}>
            <CategoryTitle>{categoryTitle}</CategoryTitle>
            {content}
        </CategoryContainer>
    );
}

// Funciones auxiliares para formatear valores
const formatNumber = (value, decimals = 2, suffix = "") => {
    if (value !== undefined && value !== null && !isNaN(parseFloat(value))) {
        return formatDecimalNumber(value, decimals) + suffix;
    }
    return "No hay datos";
};

const formatString = (value) => {
    return value && value.toString().trim() !== ""
        ? displayValue(value)
        : "No hay datos";
};

export const renderFertilizacionDataCards = (
    validations,
    datosAnalisis,
    indicadores,
    onAreasCalculatedLoader,
    chartsRef
) => {
    const variosCards = [
        {
            title: "Altura",
            value: datosAnalisis.altura?.valor
                ? formatString(datosAnalisis.altura.valor) + "snm"
                : "No hay datos",
        },
        {
            title: "Velocidad",
            value: datosAnalisis.velocidad?.valor
                ? formatString(datosAnalisis.velocidad.valor)
                : "No hay datos",
        },
        {
            title: "Código Finca",
            value: datosAnalisis.codigoFinca?.valor
                ? formatString(datosAnalisis.codigoFinca.valor)
                : "No hay datos",
        },
        {
            title: "Lote",
            value: datosAnalisis.lote?.valor
                ? formatString(datosAnalisis.lote.valor)
                : "No hay datos",
        },
        {
            title: "Nombre Operador",
            value: datosAnalisis.nombreOperador?.valor
                ? formatString(datosAnalisis.nombreOperador.valor)
                : "No hay datos",
        },
        {
            title: "Zafra",
            value: datosAnalisis.zafra?.valor
                ? formatString(datosAnalisis.zafra.valor)
                : "No hay datos",
        },
        {
            title: "Tercio",
            value: datosAnalisis.tercio?.valor
                ? formatString(datosAnalisis.tercio.valor)
                : "No hay datos",
        },
        {
            title: "Código de Máquina",
            value: datosAnalisis.codigoDeMaquina?.valor
                ? formatString(datosAnalisis.codigoDeMaquina.valor)
                : "No hay datos",
        },
        {
            title: "Eficiencia",
            value: formatNumber(datosAnalisis.eficiencia?.valor, 2),
        },
        {
            title: "Volumen Teorico",
            value: formatNumber(datosAnalisis.dosisTeorica?.valor, 2),
        },
        {
            title: "Volumen Real",
            value: formatNumber(datosAnalisis.dosisReal?.valor, 2),
        },
        {
            title: "Diferencia de Volumenes",
            // Ajusta el sufijo si deseas "%" en lugar de " L/Ha"
            value: formatNumber(datosAnalisis.diferenciaDosis?.valor, 2, " L/Ha"),
        },
        {
            title: "Ancho de Faja",
            value: formatNumber(datosAnalisis.anchoDeFaja?.valor, 2, " m"),
        },
        {
            title: "TCH Estimado",
            value: formatNumber(datosAnalisis.tchEstimado?.valor, 2),
        },
        {
            title: "Equipo",
            value: datosAnalisis.equipo?.valor
                ? formatString(datosAnalisis.equipo.valor)
                : "No hay datos",
        },
        {
            title: "Producto",
            value: datosAnalisis.producto?.valor
                ? formatString(datosAnalisis.producto.valor)
                : "No hay datos",
        },
        {
            title: "Edad (días)",
            value: formatNumber(datosAnalisis.edadDias?.valor, 0),
        },
        {
            title: "Tiempo Total",
            value: datosAnalisis.tiempoTotal?.valor
                ? formatString(datosAnalisis.tiempoTotal.valor) + "H"
                : "No hay datos",
        },
    ];

    // Cálculo de datos para las gráficas
    const totalArea =
        (indicadores.areaAplicada || 0) +
        (indicadores.areaNoAplicada || 0) +
        (indicadores.areaSobreAplicada || 0);

    const pieData = [
        {
            id: "aplicada",
            label: "aplicada",
            value: indicadores.areaAplicada || 0,
            color: "#4CAF50",
            percentage:
                totalArea > 0
                    ? ((indicadores.areaAplicada || 0) / totalArea) * 100
                    : 0,
        },
        {
            id: "sobre aplicada",
            label: "sobre aplicada",
            value: indicadores.areaSobreAplicada || 0,
            color: "#F44336",
            percentage:
                totalArea > 0
                    ? ((indicadores.areaSobreAplicada || 0) / totalArea) * 100
                    : 0,
        },
        {
            id: "no aplicada",
            label: "no aplicada",
            value: indicadores.areaNoAplicada || 0,
            color: "#FFEB3B",
            percentage:
                totalArea > 0
                    ? ((indicadores.areaNoAplicada || 0) / totalArea) * 100
                    : 0,
        },
    ];

    const barData = [
        {
            categoria: "Área aplicada en calles",
            valor: indicadores.areaSobreCalles || 0,
            color: PRIMARY_COLOR, // antes azul
        },
        {
            categoria: "Área aplicada fuera del lote",
            valor: indicadores.areaFueraDeLote || 0,
            color: "#F44336",
        },
    ];

    const charts = (
        <div style={{ position: "relative" }}>
            {/* Indicador de Área Total del Lote (primer recuadro) */}
            <AreaTotalContainer>
                {onAreasCalculatedLoader === false ? (
                    <CircularProgress size={24} />
                ) : indicadores.areaTotalLote ? (
                    <>
                        <VariationLabel>Área Total del bloque</VariationLabel>
                        <VariationValue>
                            {displayValue(indicadores.areaTotalLote)} H
                        </VariationValue>
                    </>
                ) : (
                    "No hay datos"
                )}
            </AreaTotalContainer>

            {/* Variación Dosis (segundo recuadro; top: 90px) */}
            <VariationDosisContainer>
                {onAreasCalculatedLoader === false ? (
                    <CircularProgress size={24} />
                ) : datosAnalisis.porcentajeVariacion ? (
                    <>
                        <VariationLabel>Variación Volumen</VariationLabel>
                        <VariationValue>
                            {displayValue(datosAnalisis.porcentajeVariacion?.valor)}
                        </VariationValue>
                    </>
                ) : (
                    "No hay datos"
                )}
            </VariationDosisContainer>

            {/* Variación Áreas (tercer recuadro; top: 185px) */}
            <VariationContainer>
                {onAreasCalculatedLoader === false ? (
                    <CircularProgress size={24} />
                ) : indicadores.porcentajeVariacion ? (
                    <>
                        <VariationLabel>Variación Áreas</VariationLabel>
                        <VariationValue>
                            {displayValue(indicadores.porcentajeVariacion)}
                        </VariationValue>
                    </>
                ) : (
                    "No hay datos"
                )}
            </VariationContainer>

            <ChartsRow>
                {/* Gráfica de Dona (Pie) — ECharts */}
                <ChartContainer className="fertPieChart">
                    <h4 style={{ textAlign: 'center', marginBottom: 10, fontSize: '1.5em', fontWeight: 'bold' }}>
                        Distribución de Áreas
                    </h4>
                    <ReactEChartsCore
                        echarts={echarts}
                        option={{
                            tooltip: {
                                trigger: 'item',
                                formatter: (p) =>
                                    `<strong>${p.name}</strong><br/>${formatDecimalNumber(p.value, 2)} ha<br/>${formatDecimalNumber(p.data.percentage, 2)}%`,
                                ...glassTooltip,
                            },
                            legend: {
                                top: 10,
                                left: 'center',
                                orient: 'horizontal',
                                itemGap: 14,
                                textStyle: { fontSize: 13, fontWeight: 'bold' },
                            },
                            series: [{
                                type: 'pie',
                                radius: ['40%', '70%'],
                                padAngle: 1,
                                itemStyle: { borderRadius: 3, borderWidth: 1, borderColor: '#fff' },
                                label: {
                                    show: true,
                                    position: 'inside',
                                    formatter: (p) => `${formatDecimalNumber(p.value, 2)} ha (${formatDecimalNumber(p.data.percentage, 2)}%)`,
                                    fontSize: 12,
                                    fontWeight: 'bold',
                                    color: '#333',
                                },
                                data: pieData.map(d => ({ name: d.label, value: roundToDecimals(d.value, 2), percentage: roundToDecimals(d.percentage, 2), itemStyle: { color: d.color } })),
                            }],
                        }}
                        theme="geomotica"
                        style={{ height: 340, width: '100%' }}
                        opts={{ renderer: 'canvas' }}
                        notMerge
                        lazyUpdate
                    />
                </ChartContainer>

                {/* Gráfica de Barras (Bar) — ECharts */}
                <ChartContainer className="fertBarChart">
                    <h4 style={{ textAlign: 'center', marginBottom: 10, fontSize: '1.5em', fontWeight: 'bold' }}>
                        Área Aplicada No Productiva
                    </h4>
                    <ReactEChartsCore
                        echarts={echarts}
                        option={{
                            tooltip: {
                                trigger: 'axis',
                                axisPointer: { type: 'shadow' },
                                formatter: (params) => {
                                    const p = params[0];
                                    return `<strong>${p.name}</strong><br/>${formatDecimalNumber(p.value, 2)} ha`;
                                },
                                ...glassTooltip,
                            },
                            legend: {
                                top: 10,
                                left: 'center',
                                orient: 'horizontal',
                                itemGap: 25,
                                textStyle: { fontSize: 13, fontWeight: 'bold' },
                                data: barData.map(d => d.categoria),
                            },
                            xAxis: {
                                type: 'value',
                                name: 'Hectáreas',
                                nameLocation: 'middle',
                                nameGap: 32,
                                nameTextStyle: { fontSize: 16, fontWeight: 'bold' },
                                axisLabel: { formatter: (v) => formatDecimalNumber(v, 2), fontSize: 14, fontWeight: 'bold' },
                                splitLine: { lineStyle: { type: 'dashed', color: '#E2E8F0' } },
                            },
                            yAxis: {
                                type: 'category',
                                data: barData.map(d => d.categoria),
                                axisLabel: { formatter: (v) => formatDecimalNumber(v, 2), fontSize: 14, fontWeight: 'bold' },
                                show: false,
                            },
                            series: [{
                                type: 'bar',
                                data: barData.map(d => ({
                                    value: roundToDecimals(d.valor, 2),
                                    itemStyle: { color: d.color === PRIMARY_COLOR ? PRIMARY_COLOR_HEX : d.color, borderRadius: [0, 4, 4, 0] },
                                })),
                                barWidth: '40%',
                                label: { show: true, position: 'right', fontSize: 14, fontWeight: 'bold', color: '#000', formatter: (p) => formatDecimalNumber(p.value, 2) },
                                emphasis: { itemStyle: { shadowBlur: 10, shadowColor: 'rgba(0,0,0,0.12)' } },
                            }],
                            grid: { top: 90, right: 50, bottom: 50, left: 20, containLabel: true },
                        }}
                        theme="geomotica"
                        style={{ height: 340, width: '100%' }}
                        opts={{ renderer: 'canvas' }}
                        notMerge
                        lazyUpdate
                    />
                </ChartContainer>
            </ChartsRow>
        </div>
    );

    return (
        <>
            {renderCategory("Indicadores de Área", charts, chartsRef)}
            {renderCategory(
                "Indicadores Varios",
                <CardsGrid>
                    {variosCards.map((card, index) => (
                        <DataCard key={index} title={card.title}>
                            {card.value}
                        </DataCard>
                    ))}
                </CardsGrid>
            )}
        </>
    );
};
